<template>
  <div class="product-page">
    <LazyHydrate when-visible>
      <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    </LazyHydrate>
    <LazyHydrate when-visible>
      <ProductHead mode="top" :class="{ 'mobile-hidden': hideProductHeader }" />
    </LazyHydrate>

    <div class="product-page-container">
      <transition name="badge-fade">
        <template v-if="product && product.status != 'DISCONTINUED' && !isVideoPlaying">
          <ProductBadge v-if="product.onSale" :type="'sale'" class="product-page-badge" />
          <ProductBadge v-else-if="product.onClearance" :type="'clearance'" class="product-page-badge" />
        </template>
      </transition>

      <Gallery @videoplay="isVideoPlaying = true" @videopause="isVideoPlaying = false" @changeSlide="onSlideChange" />

      <ProductFavorite :show-favorite="showFavorite" class="round" source="product-page" v-if="mounted" />

      <LazyHydrate when-visible>
        <ProductHead mode="mobile" />
      </LazyHydrate>

      <div class="delivery-row">
        <DeliveryInfo v-if="product" :key="`product-delivery-info-${product.hashKey}`" />
        <LazyHydrate when-visible>
          <ProductHead mode="desktop" />
        </LazyHydrate>
      </div>

      <Divider
        height="1px"
        margin-top="var(--space-3)"
        margin-bottom="var(--space-3)"
        color="var(--border-color)"
        class="delivery-row-bottom"
        :class="['configurator-divider']"
      />
      <LazyHydrate when-visible>
        <Configurator v-if="configurationsLength" @input="onConfiguratorChanged" />
        <Variation v-else @input="onConfiguratorChanged" />
      </LazyHydrate>
    </div>

    <div class="product-page-container">
      <Divider v-if="!configurationsLength && product && product.variation && product.variation.length > 1" class="delivery-message-top" />
    </div>

    <div class="product-page-container mobile-padding">
      <LazyHydrate when-visible>
        <ProductInfo />
      </LazyHydrate>
    </div>

    <div class="product-page-container" v-if="showCarousels && mounted">
      <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
      <Lazy>
        <CrossSell
          v-if="showCrossSell"
          :products="crossSellProducts"
          :products-per-view="4"
          :animate-cart-btn="true"
          @productAddedToCart="onCrossSellProductAdded"
        >
          <template v-slot:title>
            <h2 class="cross-sell-title">Often purchased with</h2>
          </template>
        </CrossSell>
        <RelatedProducts v-else />
      </Lazy>
    </div>

    <Lazy v-if="ugcPixleeEnabled && showUgc" class="product-page-container product-ugc-wrapper">
      <template slot-scope="{}" v-if="mounted">
        <ProductUGC
          :key="`product-gallery-${product.skuNo}`"
          :sku="product.skuNo"
          :similar-skus="similarSkus"
          @expandedVisible="onGalleryExpandedVisibleChange"
          @imagesUpdated="(images) => updateShowUgc(images && images.length)"
        ></ProductUGC>
      </template>
    </Lazy>

    <Lazy v-if="productBundleData && mounted" class="product-page-container">
      <template slot-scope="{}">
        <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
        <ProductSet
          class="product-page-product-set"
          :set="productBundleData"
          should-show-shop-all-sub-text
          @slideChange="onBundleEngaged"
          @productClick="onBundleEngaged"
          @addToCart="onBundleAddToCart"
        >
          <template v-slot:title>
            <h2 class="header-default light" v-if="productBundleData && productBundleData.title">{{ productBundleData.title }}</h2>
          </template>
        </ProductSet>
      </template>
    </Lazy>

    <div v-if="product" class="product-page-container bottom-spacer product-reviews-container" :key="`product-reviews-${product.hashKey}`">
      <LazyHydrate when-visible>
        <ProductReviews :product="product" @reviewsEngaged="onProductReviewEngaged" />
      </LazyHydrate>
      <LazyHydrate when-visible>
        <QAForm class="qa-form-container" />
      </LazyHydrate>
    </div>

    <div v-if="product" class="product-page-container" :key="`similar-products-${product.hashKey}`">
      <Divider v-if="similarProductsShown" margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
      <LazyHydrate when-visible>
        <SimilarProducts :product="product" @updated="onSimilarProductsUpdated" />
      </LazyHydrate>
    </div>

    <div class="product-footer-container">
      <LazyHydrate when-visible>
        <ProductPageFooter class="product-page-container top-spacer" />
      </LazyHydrate>
    </div>

    <CanFavorite />

    <AffirmModal :show-modal="showAffirmModal" :url="affirmModalUrl" @onClose="toggleAffirmModal" />
    <PaybrightModal :show-modal="showPaybrightModal" :url="payBrightMoreInfoUrl" @onClose="togglePaybrightModal" />
  </div>
</template>

<script>
import {
  Divider, //
  Lazy,
  ElementUtils,
  MediaUtils,
} from '@article/pixel';
import Vue from 'vue';
import { ref, onServerPrefetch, onMounted } from '@vue/composition-api';
import VueScrollTo from 'vue-scrollto';
import {
  mapState, //
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import Tracking from '@/components/commons/Tracking.js';
import ProductBadge from '@/components/commons/ProductBadge.vue';
import ProductHead from '../../components/product/ProductHead.vue';
import Gallery from '../../components/product/Gallery.vue';
import ProductFavorite from '../../components/product/ProductFavorite.vue';
import DeliveryInfo from '../../components/product/DeliveryInfo.vue';
import Variation from '../../components/product/Variation.vue';
import Configurator from '../../components/product/Configurator/Configurator.vue';
import ProductInfo from '../../components/product/ProductInfo.vue';
import ProductReviews from '@/components/reviews/ProductReviews.vue';
import RelatedProducts from '@/components/product/RelatedProducts.vue';
import SimilarProducts from '@/components/product/SimilarProducts.vue';
import CrossSell from '@/components/commons/CrossSell.vue';
import ProductPageFooter from '../../components/product/Footer.vue';
import QAForm from '@/components/product/QAForm.vue';
import buildStructuredProduct from '@/components/meta/StructuredProduct.js';
import buildSocialMetaTags from '@/components/meta/Social.js';
import { scrollTop } from '@/components/commons/utils.js';
import ProductBreadcrumb from '@/components/breadcrumb/ProductBreadcrumb.vue';
import LazyHydrate from '@/components/lazy/LazyHydrate.js';

import IterableUtils from '@/components/commons/IterableUtils.js';
import ProductStore from '@/store/ProductStore.js';
import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';

import useProductBundle from '@/compositions/useProductBundle';
import useRouter from '@/compositions/useRouter.js';
import Chronicle from '@/components/commons/Chronicle.js';

const SCROLL_TIME = 1000;
const SCROLL_OFFSET = 250;

Vue.use(VueScrollTo);

export default {
  name: 'ProductPage',

  components: {
    LazyHydrate,
    ProductHead,
    Gallery,
    ProductFavorite,
    DeliveryInfo,
    Variation,
    Configurator,
    Divider,
    ProductInfo,
    RelatedProducts,
    CrossSell,
    SimilarProducts,
    ProductUGC: () => import(/* webpackChunkName: "ProductUGC" */ '@/components/ugc/ProductUGC.vue'),
    ProductReviews,
    ProductPageFooter,
    ProductBadge,
    CanFavorite: () => import(/* webpackChunkName: "CanFavorite" */ '@/components/shared/CanFavorite.vue'),
    Lazy,
    PaybrightModal: () => import(/* webpackChunkName: "PaybrightModal" */ '@/components/cart/PaybrightModal.vue'),
    QAForm,
    Breadcrumb: ProductBreadcrumb,
    ProductSet: () => import(/* webpackChunkName: "ProductSet" */ '@/components/sets/ProductSet.vue'),
    AffirmModal: () => import(/* webpackChunkName: "AffirmModal" */ '@/components/cart/AffirmModal.vue'),
  },

  data() {
    return {
      mounted: false,
      region: '',
      deliveryTime: '',
      toShowZipForm: false,
      isLocationAccurate: null,
      isVideoPlaying: false,
      showFavorite: true,
      hideProductHeader: false,
      similarProductsShown: true,
      meta: {
        title: null,
        titleTemplate: null,
        description: null,
        script: [],
        meta: [],
      },
    };
  },

  computed: {
    ...mapState('product', [
      'product', //
      'category',
      'common',
      'carouselProducts',
      'showUgc',
      'crossSellProducts',
      'error',
    ]),

    ...mapState('common', [
      'initialized', //
      'ugcPixleeEnabled',
      'showPaybrightModal',
      'payBrightMoreInfoUrl',
      'showAffirmModal',
      'affirmModalUrl',
    ]),

    ...mapGetters('product', [
      'configurationsLength', //
      'currentNavigationCategory',
      'currentNavigationCategoryPath',
      'price',
    ]),

    similarSkus() {
      return this.product && this.product.similarSku ? this.product.similarSku.map((similar) => similar.skuNo) : [];
    },

    breadcrumb() {
      const nodes = [
        {
          title: 'Home',
          url: this.$router.resolve({ name: 'home' }).href,
        },
      ];
      const category = this.currentNavigationCategory;
      if (this.product) {
        if (category && category.parentDisplayName) {
          nodes.push({
            title: category.parentDisplayName,
            url: this.$router.resolve({ name: 'browse', params: { id: category.parentId, slug: `${category.parentSlug}` } }).href,
          });
        }
        if (category) {
          nodes.push({
            title: category.displayName,
            url: this.$router.resolve({ name: 'browse', params: { id: category.id, slug: `${category.parentSlug}-${category.slug}` } }).href,
          });
        }
        nodes.push({
          title: this.product.title,
        });
      }
      return nodes;
    },

    showCarousels() {
      return this.product?.related?.length || this.showCrossSell;
    },

    isSofaPDP() {
      return this.currentNavigationCategory.displayName.toLowerCase().indexOf('sofa') > -1;
    },
  },

  metaInfo() {
    return { ...this.meta };
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.$route.query?.forceCurrencyId) {
        LocationService.setIsLocationManual(false);
        const query = Object.assign({}, vm.$route.query);
        delete query.forceCurrencyId;

        try {
          // Server needs the forceCurrencyId query in case a redirect happens
          if (!process.env.STOREFRONT_SERVER) {
            vm.$router.replace({ query });
          }
        } catch {}
      }
    });
  },

  setup(props, context) {
    const { $store } = context.root;
    // const MIN_CROSS_SELL_COUNT = 3;

    const productBundleData = ref(null);
    const showCrossSell = ref(false);

    const { loadProductBundleProductId } = useProductBundle();
    const { router } = useRouter();

    onMounted(() => {
      /**
       * Temporarly disabled Cross Sell while new Mini-cart experiment is running.
       * Fallback to Goes Well With.
       * For more details: https://article.atlassian.net/browse/DEV-6226
       */
      // $store &&
      //   $store.watch(
      //     () => $store?.state?.product?.crossSellProducts,
      //     async () => {
      //       const crossSellProductCount = $store?.state?.product?.crossSellProducts?.length ?? 0;
      //       if (crossSellProductCount >= MIN_CROSS_SELL_COUNT) {
      //         showCrossSell.value = router?.currentRoute?.query?.crossSell ?? false;
      //         return;
      //       }
      //       showCrossSell.value = false;
      //     },
      //     {
      //       immediate: true,
      //     },
      //   );
    });

    function trackProductAddedToCart({ id, skuNo, title, productPrice }) {
      Tracking.track('CROSS_SELL_ENABLED_ADD_TO_CART', {
        product: JSON.stringify({
          id,
          skuNo,
          title,
          productPrice,
        }),
      });
    }

    function onCrossSellProductAdded(product) {
      scrollTop();
      trackProductAddedToCart(product);
    }

    onServerPrefetch(async () => {
      try {
        productBundleData.value = await loadProductBundleProductId(router.currentRoute.params.id, true);
      } catch (error) {
        Tracking.sentryCaptureError(error);
        console.error(error);
      }
    });

    onMounted(async () => {
      try {
        productBundleData.value = await loadProductBundleProductId(router.currentRoute.params.id, true);
      } catch (error) {
        Tracking.sentryCaptureError(error);
        console.error(error);
      }

      $store.watch(
        () => $store.state.product.product,
        async (newProduct, oldProduct) => {
          productBundleData.value = await loadProductBundleProductId(newProduct.activeVersionHashKey, true);
        },
      );
    });

    return {
      onCrossSellProductAdded,
      showCrossSell,
      productBundleData,
    };
  },

  async created() {
    window.addEventListener('popstate', async () => {
      await this.load({ id: this.$route.params.id });
      this.updateMeta();
      this.trackProductView();
    });
  },

  async serverPrefetch() {
    const forcePair = this.$route.query ? this.$route.query.forcePair : false;
    this.$store.registerModule('product', ProductStore.initialize({ init: true, forcePair }), { preserveState: false });

    await this.load({ id: this.$route.params.id });

    if (this.error) {
      throw new Error(this.error || `Failed to load product with id ${this.$route.params.id}`);
    } else if (this.product && this.product.hashKey && this.$route.query.preview === 'true') {
      // Allow previewing product page without redirect
    } else if (this.product && this.product.activeVersionHashKey && this.product.activeVersionHashKey !== this.product.hashKey) {
      // Redirect inactive products if an active version is available
      throw {
        status: 301,
        url: this.$router.resolve({
          name: 'product',
          params: {
            id: this.product.activeVersionHashKey,
            slug: this.product.slug,
          },
          query: this.$route.query.forcePair ? { forcePair: this.$route.query.forcePair } : {},
        }).href,
        reason: `Product ${this.product.hashKey} has newer active product version ${this.product.activeVersionHashKey}.`,
      };
    } else if (this.product && this.product.status === 'DISCONTINUED' && this.currentNavigationCategory && this.currentNavigationCategory.id) {
      const redirectCategory = this.currentNavigationCategory;
      throw {
        status: 301,
        url: this.$router.resolve({
          name: 'browse',
          params: {
            id: redirectCategory.id,
            slug: `${redirectCategory.parentSlug ? redirectCategory.parentSlug : ''}-${redirectCategory.slug}`,
          },
          query: {
            productId: this.product.hashKey,
          },
        }).href,
        reason: `Product ${this.product.hashKey} is discontinued so redirecting to category ${redirectCategory.id}.`,
      };
    } else if (this.product && this.$route.params.slug !== this.product.slug) {
      throw {
        status: 301,
        url: this.$router.resolve({
          name: 'product',
          params: { id: this.product.hashKey, slug: this.product.slug },
          query: { forceCurrencyId: this.$route.query.forceCurrencyId },
        }).href,
        reason: `The provided product slug (${this.$route.params.slug}) is different to the expected product slug (${this.product.slug}).`,
      };
    } else if (!this.product || !this.product.hashKey || !this.product.active) {
      throw {
        status: 301,
        url: '/404',
        reason: 'Product not found',
      };
    }

    this.updateMeta();
  },

  beforeMount() {
    const forcePair = this.$route.query ? !!this.$route.query.forcePair : false;
    this.$store.registerModule('product', ProductStore.initialize({ init: true, forcePair }), { preserveState: process.env.STOREFRONT });

    if (this.product && this.$route.params.slug !== this.product.slug) {
      const url = this.$router.resolve({ name: 'product', params: { id: this.product.hashKey, slug: this.product.slug } }).href;
      window.history.replaceState({}, '', url);
    }
  },

  mounted() {
    this.mounted = true;
    this.updateMeta();

    if (this.$route.query.pr) {
      this.scrollToReviewsSection();
    }

    this.trackProductView();

    const isLoggedIn = !!UserService.getEmail();
    if (isLoggedIn) {
      window.addEventListener('load', async () => {
        await IterableUtils.install();

        IterableUtils.account(process.env.ITERABLE_API_KEY);
        IterableUtils.identify(UserService.getEmail(), {});
        IterableUtils.track('Product Viewed', {
          id: this.product.skuNo,
          name: this.product.title,
          category: this.currentNavigationCategoryPath,
          brand: 'Article',
          price: this.price && this.price.final ? Math.floor(this.price.final / 100) : null,
        });
      });
    }

    const isSofaFinderEnabled = Chronicle.getFeatureStatus('sofa-finder-visible') == 'visible';
    Tracking.track('sofa-finder-view-pdp', {
      deviceType: MediaUtils.isTabletAndSmaller,
      sofaFlag: this.isSofaPDP,
      productId: this.product.hashKey,
      isSofaFinderEnabled,
    });
    Chronicle.track('sofa-finder-view-pdp', { deviceType: MediaUtils.isTabletAndSmaller, sofaFlag: this.isSofaPDP, productId: this.product.hashKey });
  },

  methods: {
    ...mapMutations('common', ['togglePaybrightModal', 'toggleAffirmModal']),

    ...mapActions('product', ['load', 'trackProductView']),

    ...mapMutations('product', ['updateShowUgc']),

    onGalleryExpandedVisibleChange(visible) {
      this.hideProductHeader = visible;
    },

    onSlideChange(type) {
      this.changeSlide(type);
    },

    onConfiguratorChanged() {
      const url = this.$router.resolve({ name: 'product', params: { id: this.product.hashKey, slug: this.product.slug } }).href;
      window.history.pushState({}, '', url);
      this.updateMeta();
      this.trackProductView();
    },

    scrollToReviewsSection() {
      this.scrollToReview(SCROLL_OFFSET, SCROLL_TIME);
    },

    changeSlide(type) {
      this.showFavorite = type === 'IMAGE';
    },

    scrollToReview(scrollOffset, scrollTime) {
      const element = document.querySelector('.product-reviews-container');
      if (element && !ElementUtils.isInViewport(element)) {
        VueScrollTo.scrollTo('.product-reviews-container', scrollTime, {
          offset: scrollOffset,
        });
      }
    },

    onSimilarProductsUpdated(products) {
      this.similarProductsShown = products.length;
    },

    updateMeta() {
      if (!this.product) {
        return;
      }

      this.meta.title = this.product.metaTitle || `${this.product.title} | Article`;
      this.meta.description = this.product.description;
      this.meta.script = [
        {
          type: 'application/ld+json',
          json: buildStructuredProduct({ product: this.product, router: this.$router }),
        },
      ];
      this.meta.meta = [
        ...buildSocialMetaTags({
          title: this.product.title,
          type: 'product',
          description: this.product.description,
          image: this.product.media && this.product.media.length ? { url: `${this.product.media[0].url}?fit=max&w={width}&q={q}` } : null,
          url: `${process.env.STOREFRONT_BASE_URL}${
            this.$router.resolve({ name: 'product', params: { id: this.product.hashKey, slug: this.product.slug } }).href
          }`,
          price: this.price.finalFormatted,
          currency: this.price.currency,
        }),
      ];
    },

    onBundleEngaged() {
      Tracking.track('PDP_BUNDLE_INTERACTION');
    },

    onBundleAddToCart() {
      Tracking.track('PDP_BUNDLE_ADD_TO_CART');
    },

    onProductReviewEngaged() {
      Tracking.track('PDP_BUNDLE_REVIEW_ENGAGEMENT', {
        containBundle: !!this.productBundleData,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@article/pixel/dist/style/variables';
@import '~@article/pixel/dist/style/mixins';

.round {
  right: var(--space-4);
}

.product-page-container {
  @include container(970px);
  padding: 0 20px;

  .extra-info-container {
    @media (max-width: $desktop-breakpoint - 1) {
      justify-content: flex-end;
      .extra-info.desktop,
      .mini-divider {
        display: none;
      }
    }
  }

  @media (max-width: $tablet-breakpoint - 1) {
    &.mobile-padding {
      padding: 0;
    }
  }

  @media (max-width: $desktop-breakpoint - 1) {
    .gallery {
      padding: 0;
      width: 100%;
    }

    .mobile-only {
      display: none;
    }
  }

  @media (min-width: $desktop-breakpoint) {
    @include container(1060px);
  }

  @media (min-width: $large-desktop-breakpoint) {
    @include container(1260px);
  }

  position: relative;

  .product-carousel-title {
    font-size: 1.5em;
  }

  .badge-fade-enter,
  .badge-fade-leave-to {
    opacity: 0;
  }

  .badge-fade-enter-active,
  .badge-fade-leave-active {
    transition: opacity 100ms ease-out;
  }
  &.similar-products-carousel {
    margin-top: 32px;
    margin-bottom: 24px;
    .favorite {
      right: -10px;
      background: transparent;
    }
  }

  .cross-sell-title {
    font-family: var(--font-light);
  }
}

.grey-background {
  background: var(--background-color);
}

.product-page {
  width: 100%;

  .reserve-breadcrumb {
    height: 24px;
    width: 100%;
  }

  .mobile-hidden {
    visibility: hidden;
  }

  .product-page-badge {
    z-index: 3;
    top: 20px;
    left: 20px;
  }

  .delivery-row {
    position: relative;
    margin-top: 12px;
  }

  .divider {
    &.delivery-message-top {
      margin: 12px 0;

      @media (max-width: $tablet-breakpoint - 1) {
        visibility: hidden;
      }
    }

    &.delivery-row-bottom {
      display: none;

      &.configurator-divider {
        display: block;
        margin: 12px 0;
      }

      @media (max-width: $tablet-breakpoint - 1) {
        display: block;
      }
    }

    &.product-info-bottom {
      @media (min-width: $tablet-breakpoint) {
        display: none;
      }
    }
  }

  .qa-form-container {
    margin-top: var(--space-10);
  }

  .delivery-message-container {
    .delivery-message-bottom {
      margin-top: 0px;
      visibility: hidden;
    }

    @media (min-width: $tablet-breakpoint) {
      background: var(--background-color);
    }
  }

  .product-ugc-wrapper {
    overflow: hidden;
  }

  .product-page-product-set {
    ::v-deep .product-set-image {
      @media (max-width: $tablet-breakpoint - 1) {
        margin: auto -20px;
      }
    }
  }
}
</style>
